<script lang="ts" setup>
import {computed} from 'vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import CompanyBadge from '@/vue/atoms/company-badge.vue';
import ConsumedMarker from '@/vue/atoms/consumed-marker.vue';
import MediaPlayBadge from '@/vue/atoms/media-play-badge.vue';
import ReccoMediaTypeAndDuration from '@/vue/molecules/recco/recco-media-type-and-duration.vue';
import {useI18n} from 'vue-i18n';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import {contentMediaType, isPlayableMediaType, recommendationTypeByMediaType, statusMarkerLabelKey} from '@/ts/utils/recco-label-keys';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import ReccoCardBase from '@/vue/molecules/recco/recommendation-cards/recco-card-base.vue';

const {t: $t} = useI18n();

const props = withDefaults(defineProps<{
  blockContent: ComponentContent;
  pageTitle?: string;
  // When true, overlay the "Recommended" marker — set only where the featured
  // item is recommendation-driven (the Explore hero).
  markRecommended?: boolean;
  // Which marker the card carries when markRecommended is on: the partner pack's featured piece
  // and the recommendation fallback occupy the same spot for different reasons (IPD-3079).
  eyebrowKey?: string;
}>(), {
  pageTitle: 'recco_explore',
  markRecommended: false,
  eyebrowKey: 'recco_recommended',
});

// Through contentMediaType, so the label, the media verb and the play badge cannot drift apart.
// It used to re-derive the precedence and only branched on audio, so a video hero read "Article".
const contentType = computed(() => recommendationTypeByMediaType[contentMediaType(props.blockContent)]);

</script>

<template>
  <div class="recommendation-card-featured">
    <recco-card-base
      v-slot="{width, idAria}"
      :block-content="blockContent"
      :current-page-title="$t(pageTitle)"
      :label-suffixes="markRecommended ? ['eyebrow'] : []"
    >
      <div class="card-content">
        <div v-if="blockContent.image" class="img-container">
          <lazy-image
            format="webp"
            :src="calculateMediaUrl(blockContent.image)"
            :width="width"
            class="img"
            loading="eager"
            :alt="blockContent.name ?? ''"
          />
          <media-play-badge v-if="isPlayableMediaType(contentMediaType(blockContent))"/>
          <consumed-marker v-if="blockContent.consumed" :media-type="contentMediaType(blockContent)"/>
          <company-badge :company-image="blockContent.companyImage"/>
        </div>
        <q-card-section class="card-section">
          <div class="description-content-container">
            <!-- Above the title rather than over the image, as on mobile: the image already
                 carries the consumed marker, and a piece someone has read is still the featured
                 one. Overlaid, the two competed and consumed won, so the label vanished exactly
                 when the content had been seen. -->
            <p v-if="markRecommended" :id="`${idAria}-eyebrow`" class="eyebrow">
              {{ $t(eyebrowKey) }}
            </p>
            <recco-media-type-and-duration class="media-type" :type="contentType" :duration="blockContent.duration"/>
            <div class="title-box">
              <span :id="idAria" class="title">
                <!-- Both are read out: they are no longer competing for one spot on the card. -->
                <span
                  v-if="blockContent.consumed"
                  class="sr-only"
                >{{ $t(statusMarkerLabelKey[contentMediaType(blockContent)]) }} </span>{{ blockContent.name }}
              </span>
            </div>
          </div>
          <div class="call-to-action">
            <span class="call-to-action-label">
              {{ $t('recco_article_read_more') }}
            </span>
            <q-icon name="arrow_forward"/>
          </div>
        </q-card-section>
      </div>
    </recco-card-base>
  </div>
</template>

<style lang="scss" scoped>
.recommendation-card-featured {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
}

:deep(.rec-link) {
  width: 100%;
}

.card-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  @include focus-visible;
  border-radius: var(--card-border-radius);
  gap: var(--Spacing-spacing-4);

  @include screen-md {
    flex-direction: row;
    gap: var(--Spacing-spacing-4);
  }
}

.img-container {
  position: relative;
  max-height: 350px;
  border-radius: var(--card-border-radius);
  overflow: hidden;
  @include min-featured-card-height;

  @include screen-md {
    @include column-width(7, 10);
  }
}

.img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  :deep(.q-img__container) {
    border-radius: var(--card-border-radius);
  }

  :deep(.q-img__image) {
    object-fit: cover;
  }

  :deep(.q-skeleton) {
    border-radius: var(--card-border-radius);
  }
}

.card-section {
  @include q-card;
  @include min-featured-card-height;
  max-height: 350px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: var(--Spacing-spacing-8) var(--Spacing-spacing-7) var(--Spacing-spacing-7) var(--Spacing-spacing-6);

  @include screen-md {
    @include column-width(3, 10);

  }
}


.description-content-container {
  display: flex;
  flex-direction: column;
}

.eyebrow {
  @include font-caption;

  margin: 0;
  padding-bottom: var(--Spacing-spacing-2);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--Neutral-UI-neutral-ui-60);
}

.media-type {
  :deep(.media-type-label) {
    @include font-caption;
  }

  padding-bottom: var(--Spacing-spacing-3);
}

.title-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: var(--Spacing-spacing-4);
}

.title {
  @include font-h3;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: var(--Spacing-spacing-4);
}

.description {
  @include font-body;
  font-weight: 500;
  color: var(--Neutral-UI-neutral-ui-70);
}

.call-to-action {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--Spacing-spacing-4);
}

.call-to-action-label {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  font-weight: 700;
}
</style>
